import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Users, Plus, Loader2, Share2, Pencil, Trash2, ChevronRight, Images, ImagePlus, X, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { nativeShare, openSms } from "@/lib/share";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const resizeImage = (file, max = 1400, quality = 0.85) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    const img = new Image();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > max || height > max) {
        if (width >= height) { height = Math.round((height * max) / width); width = max; }
        else { width = Math.round((width * max) / height); height = max; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    reader.readAsDataURL(file);
  });

export default function FamilyGroups() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [candles, setCandles] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [photoData, setPhotoData] = useState(null);
  const [cleared, setCleared] = useState(false);
  const coverRef = useRef();

  const load = useCallback(async () => {
    try {
      const [g, c] = await Promise.all([api.get("/me/groups"), api.get("/me/candles")]);
      setGroups(g.data);
      setCandles(c.data.filter((x) => x.paid));
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (user) load();
  }, [loading, user, navigate, load]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setSelected([]);
    setPhoto(null); setPhotoData(null); setCleared(false);
    setDialogOpen(true);
  };

  const openEdit = (g) => {
    setEditing(g);
    setName(g.name || "");
    setSelected(g.candle_ids || []);
    setPhoto(g.cover_url ? `${BACKEND}${g.cover_url}` : null);
    setPhotoData(null); setCleared(false);
    setDialogOpen(true);
  };

  const onCoverFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) return toast.error(t("create.photoTooBig"));
    const data = await resizeImage(file);
    setPhoto(data); setPhotoData(data); setCleared(false);
  };
  const removeCover = () => { setPhoto(null); setPhotoData(null); setCleared(true); };

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const save = async () => {
    if (selected.length === 0) return toast.error(t("family.selectAtLeastOne"));
    setSaving(true);
    try {
      const payload = { name, candle_ids: selected };
      if (photoData) payload.photo_base64 = photoData;
      else if (cleared) payload.photo_base64 = "";
      if (editing) {
        await api.patch(`/me/groups/${editing.id}`, payload);
      } else {
        await api.post("/me/groups", payload);
      }
      toast.success(t("family.saved"));
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("memorial.error"));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    if (!id) return;
    try {
      await api.delete(`/me/groups/${id}`);
      toast.success(t("family.deleted"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("memorial.error"));
    }
  };

  const groupShareMsg = (g) => {
    const names = (g.candles || []).map((c) => c.deceased_name).filter(Boolean).slice(0, 6).join(", ");
    return `🕯️ ${g.name}${names ? ` · ${names}` : ""} — ${t("family.title")}`;
  };
  const share = async (g) => {
    const link = `${BACKEND}/api/share/famille/${g.share_token}`;
    const res = await nativeShare({ title: g.name, text: groupShareMsg(g), url: link });
    if (res === "copied") toast.success(t("family.linkCopied"));
  };
  const smsShare = (g) => openSms(groupShareMsg(g), `${BACKEND}/api/share/famille/${g.share_token}`);

  return (
    <div className="min-h-[100dvh]">
      <Header />
      <div className="mx-auto max-w-3xl px-5 py-8 pb-28">
        <div className="mb-2 flex items-center justify-between gap-3">
          <h1 className="flex items-center gap-2 text-2xl font-medium">
            <Users size={22} className="text-amber-300" /> {t("family.title")}
          </h1>
          <Button className="rounded-full" onClick={openCreate} data-testid="new-group-btn">
            <Plus size={16} className="mr-1" /> {t("family.newGroup")}
          </Button>
        </div>
        <p className="mb-6 text-sm text-muted-foreground">{t("family.subtitle")}</p>

        {fetching ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-sky-400" /></div>
        ) : groups.length === 0 ? (
          <div className="rounded-3xl border border-white/10 bg-card/40 p-10 text-center" data-testid="groups-empty">
            <Images className="mx-auto mb-3 text-amber-300" />
            <p className="text-muted-foreground">{t("family.empty")}</p>
            <Button className="mt-4 rounded-full" onClick={openCreate}>{t("family.newGroup")}</Button>
          </div>
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.id} className="rounded-2xl border border-white/10 bg-card/40 p-5 transition-colors fade-in hover:border-white/25" data-testid="group-item">
                <div className="flex items-start justify-between gap-4">
                  <button type="button" onClick={() => navigate(`/souvenirs/${g.id}`)} className="flex min-w-0 flex-1 items-center gap-3 text-left" data-testid="open-group-btn">
                    {g.cover_url ? (
                      <img src={`${BACKEND}${g.cover_url}`} alt={g.name} className="h-12 w-12 shrink-0 rounded-xl border border-amber-300/60 object-cover" data-testid="group-cover-thumb" />
                    ) : (
                      <div className="flex -space-x-3">
                        {g.candles.slice(0, 4).map((c) => (
                          <img key={c.id} src={`${BACKEND}${c.photo_url}`} alt={c.deceased_name} className="h-11 w-11 shrink-0 rounded-full border-2 border-amber-300/70 object-cover" />
                        ))}
                      </div>
                    )}
                    <div className="min-w-0">
                      <h3 className="truncate text-lg font-medium">{g.name}</h3>
                      <p className="mt-0.5 truncate text-sm text-amber-200/80">
                        {t("family.candlesCount", { count: g.candle_count })} · {g.candles.map((c) => c.deceased_name).join(", ")}
                      </p>
                    </div>
                  </button>
                  <ChevronRight size={18} className="mt-3 shrink-0 text-muted-foreground" />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => share(g)} data-testid="share-group-btn">
                    <Share2 size={14} className="mr-1" /> {t("family.share")}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => smsShare(g)} data-testid="sms-group-btn">
                    <MessageCircle size={14} className="mr-1" /> SMS
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full border-white/15" onClick={() => openEdit(g)} data-testid="edit-group-btn">
                    <Pencil size={14} className="mr-1" /> {t("family.edit")}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10" onClick={() => setDeleteId(g.id)} data-testid="delete-group-btn">
                    <Trash2 size={14} className="mr-1" /> {t("family.delete")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">{editing ? t("family.editGroup") : t("family.newGroup")}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">{t("family.selectCandles")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <div className="flex flex-col items-center">
              <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={onCoverFile} data-testid="group-cover-input" />
              {photo ? (
                <div className="relative w-full">
                  <img src={photo} alt="cover" className="h-32 w-full rounded-2xl border border-amber-300/40 object-cover" data-testid="group-cover-preview" />
                  <button type="button" onClick={removeCover} className="absolute right-2 top-2 rounded-full bg-destructive p-1" data-testid="group-cover-remove"><X size={14} /></button>
                </div>
              ) : (
                <button type="button" onClick={() => coverRef.current?.click()} className="flex h-32 w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-amber-300/40 text-muted-foreground transition-colors hover:border-amber-300 hover:text-amber-300" data-testid="group-cover-add">
                  <ImagePlus size={22} />
                  <span className="mt-1.5 text-xs">{t("family.addCover")}</span>
                </button>
              )}
              <p className="mt-1.5 text-center text-[11px] text-muted-foreground">{t("family.coverPhoto")}</p>
            </div>
            <Input placeholder={t("family.groupNamePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name-input" />
            {candles.length === 0 ? (
              <p className="rounded-2xl border border-white/10 bg-card/40 p-4 text-center text-sm text-muted-foreground">{t("family.noCandles")}</p>
            ) : (
              <div className="space-y-2">
                {candles.map((c) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-3 rounded-2xl border border-white/10 bg-card/40 px-3 py-2.5 transition-colors hover:border-white/25" data-testid="group-candle-option">
                    <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`group-candle-${c.id}`} />
                    <img src={c.photo_base64 || "/candle.png"} alt={c.deceased_name} className="h-9 w-9 rounded-full border border-amber-300/60 object-cover" />
                    <span className="truncate font-medium">{c.deceased_name}</span>
                  </label>
                ))}
              </div>
            )}
            <Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="save-group-btn">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editing ? t("family.save") : t("family.createGroup"))}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="glass border-white/10" data-testid="delete-group-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("family.delete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("family.deleteConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-group-cancel">{t("family.edit") && "Annuler"}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-group-confirm">{t("family.delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
